import type * as React from "react";
import { cva, type VariantProps } from "../../lib/cva";
import type { Color } from "../../lib/colors";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import {
  ICON_REGISTRY,
  isGlyphName,
  isIconName,
  isPickerIconName,
  PICKER_ICON_REGISTRY,
  toGlyphName,
  toIconName,
  toPickerIconName,
  type GlyphName,
  type IconName,
  type PickerIconName,
} from "./icon-registry";
import type { IconComponent } from "./icon-types";

export type { GlyphName, IconName, PickerIconName };
export type { IconComponent } from "./icon-types";
export {
  isGlyphName,
  isIconName,
  isPickerIconName,
  toGlyphName,
  toIconName,
  toPickerIconName,
};

/** A registry name or compatible SVG component. */
export type IconSource = GlyphName | IconComponent;

// The compatibility names collapse onto four reviewed render combinations.
// The size-* class exempts Icon from Button and MenuItem descendant SVG pins.
export const iconVariants = cva("shrink-0", {
  variants: {
    animation: {
      spin: "animate-spin motion-reduce:animate-none",
    },
    size: {
      "2xs": "size-3.5",
      xs: "size-3.5",
      sm: "size-4",
      md: "size-4",
      lg: "size-5",
      xl: "size-6",
      "2xl": "size-6",
    },
  },
  defaultVariants: { size: "md" },
});

export type IconSize = NonNullable<VariantProps<typeof iconVariants>["size"]>;

// SalesTrak icons draw for a 24px box and are reviewed from 1.5 through 2.0
// source stroke. These pairings keep every product glyph inside that range:
// compact aliases render at 14/2, the default aliases at 16/1.75, prominent
// at 20/1.5, and display aliases at 24/1.5.
export const ICON_STROKE: Record<IconSize, number> = {
  "2xs": 2,
  xs: 2,
  sm: 1.75,
  md: 1.75,
  lg: 1.5,
  xl: 1.5,
  "2xl": 1.5,
};

export type IconColor = Color | "subtle" | "faint";

export type IconProps = Omit<
  React.SVGProps<SVGSVGElement>,
  "color" | "strokeWidth"
> &
  Omittable<{
    size?: IconSize;
    color?: IconColor;
    animation?: VariantProps<typeof iconVariants>["animation"];
  }> &
  (
    // A name from either set. An app-surface call site writes an `IconName`
    // literal; a stored entity icon arrives as a `GlyphName` (see
    // `toGlyphName`), which is the one place the picker set reaches this
    // component. A picker glyph is a filled Material Symbol and renders as
    // itself — the stroke props below are inert on it.
    | Omittable<{ name: GlyphName; as?: never }>
    | Omittable<{ as: IconComponent; name?: never }>
  );

/** The component behind a glyph name, from whichever set owns it. An unknown
 *  name (one that left a registry) falls back to the circle rather than
 *  crashing. */
export function glyphComponent(name: GlyphName): IconComponent {
  if (isPickerIconName(name)) return PICKER_ICON_REGISTRY[name];
  return ICON_REGISTRY[name as IconName] ?? ICON_REGISTRY.circle;
}

export function Icon({
  name,
  as: AsIcon,
  size,
  color,
  animation,
  className,
  "aria-label": ariaLabel,
  ...props
}: IconProps): React.ReactElement {
  const Glyph = name ? glyphComponent(name) : AsIcon!;
  const dataColor =
    color === "subtle" || color === "faint" ? undefined : color;

  return (
    <Glyph
      className={cn(
        iconVariants({ animation, size }),
        color === "subtle" && "text-muted-foreground/72",
        color === "faint" && "text-faint-foreground",
        dataColor && "text-c",
        className,
      )}
      data-color={dataColor}
      data-icon-name={name}
      data-icon-style={
        name && isPickerIconName(name) ? "material-filled" : "salestrak-outline"
      }
      data-slot="icon"
      strokeWidth={ICON_STROKE[size ?? "md"]}
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      {...props}
    />
  );
}
